/* eslint-disable eqeqeq */
import React, { useRef, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import {
  getValidationPairsForTranslatableText,
  getValidationShapeForTranslatableText,
  TranslationFieldOptions,
} from 'utils/schemaHelper';
import { TranslatableInput } from 'components/formInputs/tranlatableInput';
import { FormDialog } from 'components/dialogs/formDialog';
import { CheckboxWrapper } from 'components/formInputs/wrappers/checkboxWrapper';
import { TextboxWrapper } from 'components/formInputs/wrappers/textboxWrapper';
import { DropDownList } from 'components/formInputs/dropDownList';
import { getTranslatedData } from 'locales';

export const VariationJoinsOptionAddEditForm = ({
  itemData,
  isOpen,
  closeModal,
  onSave,
  isAttached,
  allProducts,
}) => {
  const [isProduct, setIsProduct] = useState(false);
  const [overridePrice, setOverridePrice] = useState(false);

  const schema = yup.object().shape({
    id: yup.string().default(itemData?.id),
    name: yup
      .object()
      .shape(
        getValidationShapeForTranslatableText(
          itemData?.name,
          TranslationFieldOptions.REQUIRE_AT_LEAST_ONE,
        ),
        getValidationPairsForTranslatableText(),
      ),
    is_product: yup.boolean().default(itemData?.is_product || false),
    rkeeper_group_id: yup.string().nullable().default(itemData?.rkeeper_group_id),
    rkeeper_code: yup.string().nullable().default(itemData?.rkeeper_code),
    hidden: yup.boolean().default(itemData?.hidden || false),
    hidden_dinein: yup.boolean().default(itemData?.hidden_dinein || false),
    hidden_delivery: yup.boolean().default(itemData?.hidden_delivery || false),
    override_price: yup.boolean().default(itemData?.override_price || false),
    price: yup
      .number()
      .min(0)
      .max(999)
      .nullable(true)
      // checking self-equality works for NaN, transforming it to null
      .transform((_, val) => (val == Number(val) ? Number(val) : null))
      .default(itemData?.price),
    combo_product: yup.string().nullable().default(itemData?.combo_product),
  });

  const cancelButtonRef = useRef(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    reset,
    setValue,
  } = useForm({
    defaultValues: schema.default(),
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (itemData) {
      setIsProduct(itemData.is_product);
      setOverridePrice(itemData.override_price);
    }

    reset(itemData);
  }, [itemData, reset]);

  const onFormSubmit = async (data) => {
    const submitedData = {
      ...data,
      rkeeper_code: data.rkeeper_code ? data.rkeeper_code.trim() : '',
      rkeeper_group_id: data.rkeeper_group_id ? data.rkeeper_group_id.trim() : '',
      id: data.id || uuidv4(),
    };

    delete submitedData.isEditForm;

    onSave(submitedData);
  };

  const onError = (data) => console.log(data);

  return (
    <FormDialog
      isOpen={isOpen}
      cancelButtonRef={cancelButtonRef}
      onClose={closeModal}
      title={itemData ? 'Edit' : 'Add New'}
      buttons={[
        { text: 'Save', type: 'submit', color: 'blue' },
        { text: 'Cancel', onClick: closeModal, color: 'gray' },
      ]}
      onSubmit={handleSubmit(onFormSubmit, onError)}
      small
    >
      <div className="grid grid-cols-6 gap-6 text-left">
        <div className="col-span-6">
          <TranslatableInput
            name="name"
            register={register}
            error={errors?.name}
            containerClassName="sm:col-span-3"
            labelClassName="block text-sm font-medium text-gray-700"
            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
            placeholder="Option name"
            onChange={() => {
              if (errors?.name) {
                clearErrors('name');
              }
            }}
            disabled={isAttached}
          />
        </div>

        <CheckboxWrapper
          register={register}
          name="is_product"
          title="Is product"
          subtitle="check if this is a product, not variation"
          responsiveClassName="col-span-6"
          onChange={(e) => {
            setIsProduct(e.target.checked);

            if (e.target.checked) {
              setValue('rkeeper_group_id', null);
              setValue('rkeeper_code', null);
            } else {
              setValue('combo_product', null);
            }
          }}
        />

        {!isProduct && (
        <>
          <TextboxWrapper
            register={register}
            name="rkeeper_group_id"
            title="External group ID"
            responsiveClassName="col-span-6"
            error={errors?.rkeeper_group_id}
          />
          <TextboxWrapper
            register={register}
            name="rkeeper_code"
            title={isProduct ? 'External product ID' : 'External option ID'}
            responsiveClassName="col-span-6"
            error={errors?.rkeeper_code}
          />
        </>
        )}

        {isProduct && (
        <div className="col-span-6">
          <DropDownList
            {...register('combo_product')}
            showLabel
            id="combo_product"
            name="combo_product*"
            label="Combo product"
            subtitle="Display product as a variation option"
            options={allProducts && allProducts.length > 0 ? [{ label: '-', value: '' }, ...allProducts.map((x) => ({ label: getTranslatedData(x.name), value: x._id }))] : [{ label: '-', value: '' }]}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            onChange={(e) => {
              setValue('combo_product', e.target.value);
              clearErrors();
            }}
          />
        </div>
        )}

        <CheckboxWrapper
          register={register}
          name="override_price"
          title="Override price"
          subtitle="check to override the price"
          responsiveClassName="col-span-6"
          onChange={(e) => setOverridePrice(e.target.checked)}
        />

        {overridePrice && (
        <TextboxWrapper
          register={register}
          name="price"
          title="Price"
          responsiveClassName="col-span-6"
          error={errors?.price}
        />
        )}

        <CheckboxWrapper
          register={register}
          name="hidden"
          title="Hidden"
          subtitle="hidden everywhere"
          responsiveClassName="col-span-6 sm:col-span-3"
        />
        <CheckboxWrapper
          register={register}
          name="hidden_dinein"
          title="Hidden dine-in"
          subtitle="hidden only in dine-in"
          responsiveClassName="col-span-6 sm:col-span-3"
          disabled={isAttached}
        />
        <CheckboxWrapper
          register={register}
          name="hidden_delivery"
          title="Hidden delivery"
          subtitle="hidden only in delivery/takeaway"
          responsiveClassName="col-span-6 sm:col-span-3"
          disabled={isAttached}
        />
      </div>
    </FormDialog>
  );
};
